import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Target, TrendingUp, Lock, Unlock, Send, Mail, Info } from 'lucide-react';
import { getWeekGoals, saveGoals, getGoalsVsActual, requestGoalsUnlock, getWeeklyDefaults } from '../utils/api';
import { getCurrentWeek, getPreviousWeek, getNextWeek, formatDateDisplay, isCurrentWeek } from '../utils/weekUtils';
import { toast } from 'sonner';
import WeekCalendar from '../components/WeekCalendar';
import AgentLayout from '../components/AgentLayout';
import SendReportModal from '../components/SendReportModal';
import ReportInfoBlock from '../components/ReportInfoBlock';

const GOAL_FIELDS = [
  { key: 'leadsGoal', label: 'Expected\nLeads', type: 'int' },
  { key: 'doorKnocksGoal', label: 'Expected\nDoor Knocks', type: 'int' },
  { key: 'phoneCallsGoal', label: 'Expected\nPhone Calls', type: 'int' },
  { key: 'presentationsGoal', label: 'Expected\nPresentations', type: 'int' },
  { key: 'salesGoal', label: 'Expected\nSales', type: 'int', highlight: true },
  { key: 'futureAppointmentsGoal', label: 'Expected\nFuture Appts', type: 'int' },
  { key: 'annualizedPremiumGoal', label: 'Expected\nAnnual Premium', type: 'float' },
  { key: 'referralsReceivedGoal', label: 'Expected\nReferrals to\nReceive', type: 'int' },
  { key: 'referralsContactedGoal', label: 'Expected\nReferrals to\nContact', type: 'int' },
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const WeeklyGoals = () => {
  const navigate = useNavigate();
  const [currentWeekStart, setCurrentWeekStart] = useState('');
  const [currentWeekEnd, setCurrentWeekEnd] = useState('');
  const [dailyGoals, setDailyGoals] = useState([]);
  const [goalStatus, setGoalStatus] = useState('draft');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [showCalendar, setShowCalendar] = useState(true);
  const [unlockRequested, setUnlockRequested] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [reportMeta, setReportMeta] = useState({ weeklyApGoal: '', areasWorked: '' });

  const token = localStorage.getItem('agentToken');

  useEffect(() => {
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    loadCurrentWeekGoals();
  }, [navigate]);

  const loadCurrentWeekGoals = async () => {
    setIsLoading(true);
    try {
      const week = getCurrentWeek();
      setCurrentWeekStart(week.start);
      setCurrentWeekEnd(week.end);
      await loadGoals(week.start);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGoals = async (weekStart) => {
    try {
      const result = await getWeekGoals(weekStart, token);
      if (result.exists && result.goals) {
        setGoalStatus(result.goals.status || 'draft');
        setReportMeta(result.goals.reportMeta || { weeklyApGoal: '', areasWorked: '' });
        if (result.goals.dailyGoals && result.goals.dailyGoals.length > 0) {
          const normalized = result.goals.dailyGoals.map(d => ({
            ...d,
            offDay: d.offDay !== undefined ? d.offDay : (d.activeDay !== undefined ? !d.activeDay : false),
          }));
          const withDefaults = await applyDefaultsToGoals(normalized);
          setDailyGoals(withDefaults);
        } else {
          const fresh = createBlankGoals(weekStart);
          const withDefaults = await applyDefaultsToGoals(fresh);
          setDailyGoals(withDefaults);
        }
      } else {
        setGoalStatus('draft');
        const fresh = createBlankGoals(weekStart);
        const withDefaults = await applyDefaultsToGoals(fresh);
        setDailyGoals(withDefaults);
      }
    } catch (error) {
      console.error('Error loading goals:', error);
      initializeDailyGoals(weekStart);
    }
  };

  const createBlankGoals = (weekStart) => {
    const sunday = new Date(weekStart + 'T00:00:00');
    return DAY_NAMES.map((_, index) => {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + index);
      return {
        date: date.toISOString().split('T')[0],
        offDay: false,
        leadsGoal: 0, doorKnocksGoal: 0, phoneCallsGoal: 0,
        presentationsGoal: 0, salesGoal: 0, futureAppointmentsGoal: 0,
        annualizedPremiumGoal: 0, referralsReceivedGoal: 0, referralsContactedGoal: 0,
      };
    });
  };

  const initializeDailyGoals = (weekStart) => {
    setDailyGoals(createBlankGoals(weekStart));
  };

  const applyDefaultsToGoals = async (goals) => {
    const isBlank = goals.every(day => !day.offDay && GOAL_FIELDS.every(f => (day[f.key] || 0) === 0));
    if (!isBlank) return goals;
    try {
      const res = await getWeeklyDefaults(token);
      if (res.goalsDefaults && res.goalsDefaults.length === 7) {
        return goals.map((day, i) => {
          const def = res.goalsDefaults[i];
          if (!def) return day;
          const result = { ...day, offDay: def.offDay || false };
          GOAL_FIELDS.forEach(f => {
            result[f.key] = def.offDay ? 0 : (def[f.key] || 0);
          });
          return result;
        });
      }
    } catch (e) { /* defaults not set */ }
    return goals;
  };

  const navigateWeek = (weekStart) => {
    const sunday = new Date(weekStart + 'T00:00:00');
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setCurrentWeekStart(fmt(sunday));
    setCurrentWeekEnd(fmt(saturday));
    loadGoals(fmt(sunday));
    setShowComparison(false);
    setUnlockRequested(false);
  };

  const handlePreviousWeek = () => navigateWeek(getPreviousWeek(currentWeekStart).start);
  const handleNextWeek = () => navigateWeek(getNextWeek(currentWeekStart).start);
  const handleThisWeek = () => { loadCurrentWeekGoals(); setShowComparison(false); };
  const handleCalendarWeekSelect = (start) => { navigateWeek(start); };

  const updateDailyGoal = (dayIndex, field, value) => {
    setDailyGoals(prev => {
      const updated = [...prev];
      if (field === 'offDay') {
        updated[dayIndex] = {
          ...updated[dayIndex],
          offDay: value,
          // Zero out all fields when marking as off
          ...(value ? GOAL_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: 0 }), {}) : {}),
        };
      } else {
        updated[dayIndex] = {
          ...updated[dayIndex],
          [field]: field === 'annualizedPremiumGoal' ? (parseFloat(value) || 0) : (parseInt(value) || 0),
        };
      }
      return updated;
    });
  };

  // Auto-calculate weekly totals
  const weeklyTotals = useMemo(() => {
    const totals = {};
    GOAL_FIELDS.forEach(f => { totals[f.key] = 0; });
    dailyGoals.forEach(day => {
      if (!day.offDay) {
        GOAL_FIELDS.forEach(f => {
          totals[f.key] += (day[f.key] || 0);
        });
      }
    });
    return totals;
  }, [dailyGoals]);

  const handleSubmitAndLock = async () => {
    setIsSaving(true);
    try {
      const goalsData = {
        weekStartDate: currentWeekStart,
        weekEndDate: currentWeekEnd,
        mode: 'planner',
        dailyGoals,
        status: 'locked',
        reportMeta,
      };
      const result = await saveGoals(goalsData, token);
      if (result.success) {
        setGoalStatus('locked');
        toast.success('Goals submitted & locked!');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save goals';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const goalsData = {
        weekStartDate: currentWeekStart,
        weekEndDate: currentWeekEnd,
        mode: 'planner',
        dailyGoals,
        status: 'draft',
        reportMeta,
      };
      const result = await saveGoals(goalsData, token);
      if (result.success) toast.success('Draft saved!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save goals';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewComparison = async () => {
    try {
      const result = await getGoalsVsActual(currentWeekStart, token);
      if (!result.hasGoals) { toast.error('No goals set for this week'); return; }
      if (!result.hasActivity) { toast.error('No activity report for this week'); return; }
      setComparison(result.comparison);
      setShowComparison(true);
    } catch (error) {
      toast.error('Failed to load comparison');
    }
  };

  const handleRequestUnlock = async () => {
    try {
      await requestGoalsUnlock(currentWeekStart, unlockReason, token);
      toast.success('Unlock request sent! An admin will review it shortly.');
      setUnlockRequested(true);
      setShowUnlockModal(false);
      setUnlockReason('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send unlock request';
      if (msg.includes('already pending')) {
        setUnlockRequested(true);
        setShowUnlockModal(false);
      }
      toast.error(msg);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Goal Achieved': return 'bg-green-100 text-green-800';
      case 'On Track': return 'bg-blue-100 text-blue-800';
      case 'Needs Attention': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isLocked = goalStatus === 'locked';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const formatDayDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <AgentLayout title="Weekly Goal Planner">
      <div data-testid="weekly-goals-page">

        {/* Merged Header Bar */}
        <div className="bg-white rounded-lg shadow-md p-3 mb-3" data-testid="goals-header">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            {/* Week Navigation */}
            <div className="flex items-center gap-2">
              <button onClick={handlePreviousWeek} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition" data-testid="prev-week-btn">
                <ChevronLeft size={18} />
              </button>
              <div className="text-center min-w-[200px]">
                <div className="flex items-center gap-2 justify-center">
                  <Target size={16} className="text-brand-orange" />
                  <h2 className="text-base font-bold text-brand-blue" data-testid="week-date-range">
                    {formatDateDisplay(currentWeekStart)} - {formatDateDisplay(currentWeekEnd)}
                  </h2>
                </div>
                {isCurrentWeek(currentWeekStart) && (
                  <p className="text-xs text-brand-orange font-semibold">Current Week</p>
                )}
              </div>
              <button onClick={handleNextWeek} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition" data-testid="next-week-btn">
                <ChevronRight size={18} />
              </button>
              {!isCurrentWeek(currentWeekStart) && (
                <button onClick={handleThisWeek} className="px-3 py-1.5 bg-brand-orange text-white text-xs rounded-md hover:bg-orange-600 transition font-semibold" data-testid="this-week-btn">
                  This Week
                </button>
              )}
              <button
                onClick={() => setShowCalendar(!showCalendar)}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition text-gray-600"
                data-testid="toggle-calendar-btn"
                title={showCalendar ? 'Hide Calendar' : 'Show Calendar'}
              >
                <span className="text-xs font-semibold">{showCalendar ? 'Hide Cal' : 'Show Cal'}</span>
              </button>
            </div>

            {/* Status + Summary Stats */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Status badge */}
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${isLocked ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-testid="goals-status-badge">
                {isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                {isLocked ? 'Locked' : 'Draft'}
              </div>
              {/* Quick totals */}
              <div className="text-center">
                <div className="text-lg font-bold text-brand-blue">{weeklyTotals.salesGoal}</div>
                <div className="text-[10px] text-gray-500 font-medium">Exp. Sales</div>
              </div>
              <div className="w-px h-6 bg-gray-200 hidden sm:block"></div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">${weeklyTotals.annualizedPremiumGoal.toFixed(0)}</div>
                <div className="text-[10px] text-gray-500 font-medium">Exp. Premium</div>
              </div>
              <div className="w-px h-6 bg-gray-200 hidden sm:block"></div>
              <div className="text-center">
                <div className="text-lg font-bold text-brand-orange">{weeklyTotals.presentationsGoal}</div>
                <div className="text-[10px] text-gray-500 font-medium">Exp. Pres.</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button onClick={() => setShowSendModal(true)} className="flex items-center gap-1.5 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition font-semibold text-xs" data-testid="open-send-goals-btn">
                <Mail size={14} /> Send Report
              </button>
              <button onClick={handleViewComparison} className="flex items-center gap-1.5 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition font-semibold text-xs" data-testid="goals-vs-actual-btn">
                <TrendingUp size={14} /> Goals vs Actual
              </button>
              {isLocked ? (
                !unlockRequested ? (
                  <button onClick={() => setShowUnlockModal(true)} className="flex items-center gap-1.5 bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 transition font-semibold text-xs" data-testid="request-unlock-top-btn">
                    <Unlock size={14} /> Request Unlock
                  </button>
                ) : (
                  <span className="flex items-center gap-1 px-3 py-2 bg-yellow-100 text-yellow-700 rounded-md text-xs font-semibold"><Send size={12} /> Pending</span>
                )
              ) : (
                <>
                  <button onClick={handleSaveDraft} disabled={isSaving} className="flex items-center gap-1.5 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition font-semibold text-xs disabled:opacity-50" data-testid="save-draft-btn">
                    {isSaving ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button onClick={handleSubmitAndLock} disabled={isSaving} className="flex items-center gap-1.5 bg-brand-blue text-white px-4 py-2 rounded-md hover:bg-brand-orange transition font-semibold text-xs disabled:opacity-50" data-testid="submit-lock-btn">
                    <Lock size={14} /> {isSaving ? 'Saving...' : 'Submit & Lock Goals'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Collapsible Calendar + Info Block + Reminder */}
        {showCalendar && (
          <div className="mb-3 flex gap-3 items-start" data-testid="calendar-container">
            <WeekCalendar
              weekStart={currentWeekStart}
              weekEnd={currentWeekEnd}
              onWeekSelect={handleCalendarWeekSelect}
            />
            <ReportInfoBlock
              agentName={agentInfo?.fullName || 'Agent'}
              dateLabel="Week of"
              dateValue={`${formatDateDisplay(currentWeekStart)} - ${formatDateDisplay(currentWeekEnd)}`}
              goalLabel="Weekly AP Goal"
              goalValue={reportMeta.weeklyApGoal}
              onGoalChange={(v) => { setReportMeta(prev => ({ ...prev, weeklyApGoal: v })); }}
              areasWorked={reportMeta.areasWorked}
              onAreasWorkedChange={(v) => { setReportMeta(prev => ({ ...prev, areasWorked: v })); }}
            />
            <div className="hidden lg:flex flex-col bg-white rounded-lg shadow-md p-4 min-w-[260px] ml-auto" data-testid="goals-reminder-box">
              <h3 className="text-sm font-bold text-brand-blue mb-3 flex items-center gap-1.5">
                <Info size={14} /> Reminders
              </h3>
              <ul className="space-y-3 text-xs text-gray-600 flex-1">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                  <span>Set <strong>expected</strong> values for each day</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-orange mt-1.5 flex-shrink-0" />
                  <span><strong>Submit & Lock</strong> when you're done planning</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-blue mt-1.5 flex-shrink-0" />
                  <span>Set up your weekly goal defaults in the <a href="/agent/settings" className="text-brand-blue font-semibold hover:underline">Settings</a> tab to pre-fill goals each week</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 flex-shrink-0" />
                  <span>Compare with <strong>Goals vs Actual</strong> after the week</span>
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                Status: <span className={`font-semibold ${isLocked ? 'text-green-600' : 'text-yellow-600'}`}>{isLocked ? 'Locked' : 'Draft'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Goals vs Actual Comparison */}
        {showComparison && comparison && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-3" data-testid="goals-vs-actual-table">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-brand-blue">Goals vs Actual Performance</h2>
              <button onClick={() => setShowComparison(false)} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1 bg-gray-100 rounded-md">Close</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">Metric</th>
                    <th className="px-4 py-3 text-center font-bold">Goal</th>
                    <th className="px-4 py-3 text-center font-bold">Actual</th>
                    <th className="px-4 py-3 text-center font-bold">Difference</th>
                    <th className="px-4 py-3 text-center font-bold">% Achieved</th>
                    <th className="px-4 py-3 text-center font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(comparison).map(([key, data]) => (
                    <tr key={key} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</td>
                      <td className="px-4 py-3 text-center">{data.goal}</td>
                      <td className="px-4 py-3 text-center font-bold">{data.actual}</td>
                      <td className={`px-4 py-3 text-center font-semibold ${data.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {data.difference >= 0 ? '+' : ''}{data.difference}
                      </td>
                      <td className="px-4 py-3 text-center font-semibold">
                        {data.percentAchieved !== null ? `${data.percentAchieved.toFixed(1)}%` : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(data.status)}`}>{data.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Weekly Goal Planner Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-3" data-testid="goal-planner-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b-2 border-gray-300">
                  <th className="px-2 py-2 text-center font-bold text-xs">Off</th>
                  <th className="px-2 py-2 text-center font-bold text-xs">Day</th>
                  {GOAL_FIELDS.map(f => (
                    <th key={f.key} className="px-2 py-2 text-center font-bold text-xs whitespace-pre-line">
                      {f.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dailyGoals.map((day, index) => (
                  <tr
                    key={index}
                    className={`border-b hover:bg-gray-50 ${day.offDay ? 'bg-gray-100 opacity-60' : ''} ${isLocked ? 'cursor-not-allowed' : ''}`}
                    data-testid={`goal-row-${DAY_NAMES[index]?.toLowerCase()}`}
                  >
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={day.offDay}
                        onChange={(e) => updateDailyGoal(index, 'offDay', e.target.checked)}
                        disabled={isLocked}
                        className="w-4 h-4 cursor-pointer"
                        data-testid={`offday-goal-${index}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 font-semibold whitespace-nowrap text-xs">
                      {DAY_NAMES[index]}
                      <span className="text-gray-400 font-normal ml-1">{formatDayDate(day.date)}</span>
                    </td>
                    {GOAL_FIELDS.map(f => (
                      <td key={f.key} className="px-1 py-1.5">
                        <input
                          type="number"
                          step={f.type === 'float' ? '0.01' : '1'}
                          value={day[f.key]}
                          onChange={(e) => updateDailyGoal(index, f.key, e.target.value)}
                          disabled={day.offDay || isLocked}
                          className={`w-16 px-1 py-1 border rounded text-center text-sm disabled:bg-gray-100 ${f.highlight && !day.offDay ? 'bg-green-50' : ''} ${f.type === 'float' ? 'w-20' : ''}`}
                          min="0"
                          data-testid={`goal-${f.key}-${index}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Auto-Calculated Weekly Totals Row */}
                <tr className="bg-brand-blue text-white font-bold" data-testid="weekly-totals-row">
                  <td colSpan="2" className="px-2 py-2 text-right text-xs">WEEKLY TOTALS:</td>
                  {GOAL_FIELDS.map(f => (
                    <td key={f.key} className={`px-2 py-2 text-center text-xs ${f.highlight ? 'bg-green-600' : ''}`}>
                      {f.type === 'float' ? `$${weeklyTotals[f.key].toFixed(2)}` : weeklyTotals[f.key]}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-between" data-testid="goals-bottom-actions">
          <div className="text-sm text-gray-500">
            {isLocked ? (
              <span className="flex items-center gap-1.5 text-green-600 font-semibold"><Lock size={14} /> Goals are locked for this week.</span>
            ) : (
              <span className="text-gray-400">Fill in your projected goals for the week, then submit & lock.</span>
            )}
          </div>
          {isLocked ? (
            <div>
              {unlockRequested ? (
                <span className="flex items-center gap-1.5 px-4 py-2.5 bg-yellow-100 text-yellow-700 rounded-md font-semibold text-sm" data-testid="unlock-requested-badge">
                  <Send size={14} /> Unlock request sent - waiting for admin
                </span>
              ) : (
                <button
                  onClick={() => setShowUnlockModal(true)}
                  className="flex items-center gap-1.5 bg-yellow-500 text-white px-5 py-2.5 rounded-md hover:bg-yellow-600 transition font-semibold text-sm min-h-[44px]"
                  data-testid="request-unlock-btn"
                >
                  <Unlock size={16} /> Request Unlock
                </button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleSaveDraft} disabled={isSaving} className="flex items-center gap-1.5 bg-gray-200 text-gray-700 px-4 py-2.5 rounded-md hover:bg-gray-300 transition font-semibold text-sm disabled:opacity-50 min-h-[44px]" data-testid="save-draft-bottom-btn">
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>
              <button onClick={handleSubmitAndLock} disabled={isSaving} className="flex items-center gap-1.5 bg-brand-blue text-white px-6 py-2.5 rounded-md hover:bg-brand-orange transition font-semibold text-sm disabled:opacity-50 min-h-[44px]" data-testid="submit-lock-bottom-btn">
                <Lock size={16} /> {isSaving ? 'Saving...' : 'Submit & Lock Goals'}
              </button>
            </div>
          )}
        </div>

        {/* Request Unlock Modal */}
        {showUnlockModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="unlock-request-modal">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Request Unlock</h3>
              <p className="text-sm text-gray-500 mb-4">
                Your goals for <strong>{formatDateDisplay(currentWeekStart)} - {formatDateDisplay(currentWeekEnd)}</strong> are locked. Submit a request for your admin to unlock them.
              </p>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue mb-4"
                placeholder="E.g., schedule changed, updated availability..."
                data-testid="unlock-reason-input"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowUnlockModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition font-semibold text-sm" data-testid="cancel-unlock-request">
                  Cancel
                </button>
                <button onClick={handleRequestUnlock} className="flex items-center gap-1.5 px-5 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition font-semibold text-sm" data-testid="submit-unlock-request">
                  <Send size={14} /> Send Request
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      <SendReportModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        reportType="goals"
        weekStartDate={currentWeekStart}
      />
    </AgentLayout>
  );
};

export default WeeklyGoals;
